import React, { useState, useContext } from "react";
import { Container, Row, Col, Card, Form, Button } from "react-bootstrap";
import MaterialTable from "material-table";
import { useHistory } from "react-router-dom";
import ApiService from "../ApiService";
import { parseAxiosError } from "../util/parserHelper";
import PageTitle from "./Common/PageTitle/PageTitle";
import { SnackbarContext } from "./AlertSystem";
import "./PatientSearchPage.css";

const PatientSearchPage = () => {
  // States for each filter input
  const [emailSearch, setEmailSearch] = useState("");
  const [dobSearch, setDobSearch] = useState("");
  const [phoneSearch, setPhoneSearch] = useState("");
  const [kitSearch, setKitSearch] = useState("");
  const [nameSearch, setNameSearch] = useState("");
  const [orderIdSearch, setOrderIdSearch] = useState("");
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);

  const { showSnackbar } = useContext(SnackbarContext);

  // useHistory for navigation
  const navigate = useHistory();

  // Show a snack message if needed
  const handleSnackOpen = (text, severity = "info") => {
    showSnackbar?.({ message: text, severity, autoHide: 6000 });
  };

  // Navigate to the patient profile page
  const navigatePatientProfile = (id, data) => {
    navigate.push({
      pathname: "PatientProfile",
      search: `?patientId=${id}&idType=sub`,
    });
  };

  // Multi-filter search that fires an API call for each active filter
  const handleMultiFilterSearch = async () => {
    // Build an array of API call promises based on which filters have values.
    const promises = [];

    if (emailSearch.trim()) {
      promises.push(
        ApiService.searchPatient({
          type: "patient_email",
          term: emailSearch.toLowerCase(),
        })
      );
    }
    if (dobSearch.trim()) {
      promises.push(
        ApiService.searchPatient({
          type: "patient_dob",
          term: dobSearch,
        })
      );
    }
    if (phoneSearch.trim()) {
      const normalizedPhone = phoneSearch.replace(/\D/g, '').slice(-10); // remove non-digits and get last 10 digits
      promises.push(
        ApiService.searchPatient({
          type: "phone",
          term: normalizedPhone,
        })
      );
    }
    if (kitSearch.trim()) {
      promises.push(
        ApiService.searchPatient({
          type: "patient_kit_id",
          term: kitSearch,
        })
      );
    }
    if (orderIdSearch.trim()) {
      promises.push(
        ApiService.searchPatient({
          type: "order_id",
          term: orderIdSearch,
        })
      );
    }
    if (nameSearch.trim()) {
      promises.push(
        ApiService.searchPatient({
          type: "patient_name",
          term: nameSearch,
        })
      );
    }

    // If no filter is provided, show a snack message and exit.
    if (promises.length === 0) {
      handleSnackOpen("Please enter at least one search criteria.", "error");
      return;
    }

    try {
      setLoading(true);
      // Fire all API calls concurrently.
      const responses = await Promise.all(promises);
      let combinedResults = [];

      responses.forEach((response) => {
        if (response?.data) {
          combinedResults = combinedResults.concat(response.data);
        }
      });

      // Remove duplicate records by patient 'id'
      const uniqueResults = [
        ...new Map(combinedResults.map((item) => [item.id, item])).values(),
      ];

      setTableData(uniqueResults);

      if (uniqueResults.length === 0) {
        handleSnackOpen("No patients found for the provided filters.", "warning");
      }
    } catch (error) {
      handleSnackOpen(
        `Patient search failed. ${parseAxiosError(error)?.message}`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Clear all filters and table data
  const handleClearFilters = () => {
    setEmailSearch("");
    setDobSearch("");
    setPhoneSearch("");
    setKitSearch("");
    setNameSearch("");
    setOrderIdSearch("");
    setTableData([]);
  };

  return (
    <Container fluid>
      <PageTitle titleName={"Search Patient"} />
      <Row>
        <Col md={12}>
          <Card className="patient-search-card">
            <Card.Body>
              <Form className="patient-search-form">
                <div className="filter-grid">
                  <Form.Group controlId="emailSearch" className="filter-field">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Type patient email"
                      value={emailSearch}
                      onChange={(e) => setEmailSearch(e.target.value)}
                    />
                  </Form.Group>
                  <Form.Group controlId="dobSearch" className="filter-field">
                    <Form.Label>DOB</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="YYYY-MM-DD"
                      value={dobSearch}
                      onChange={(e) => setDobSearch(e.target.value)}
                    />
                  </Form.Group>
                  <Form.Group controlId="phoneSearch" className="filter-field">
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Type patient phone"
                      value={phoneSearch}
                      onChange={(e) => setPhoneSearch(e.target.value)}
                    />
                  </Form.Group>
                  <Form.Group controlId="kitSearch" className="filter-field">
                    <Form.Label>Kit Id</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Type kit id"
                      value={kitSearch}
                      onChange={(e) => setKitSearch(e.target.value)}
                    />
                  </Form.Group>
                  <Form.Group controlId="nameSearch" className="filter-field">
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Type patient name"
                      value={nameSearch}
                      onChange={(e) => setNameSearch(e.target.value)}
                    />
                  </Form.Group>
                  <Form.Group controlId="orderIdSearch" className="filter-field">
                    <Form.Label>Master/Order ID</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Type order id"
                      value={orderIdSearch}
                      onChange={(e) => setOrderIdSearch(e.target.value)}
                    />
                  </Form.Group>
                </div>
                <div className="filter-actions">
                  <Button
                    type="button"
                    variant="primary"
                    onClick={handleMultiFilterSearch}
                    disabled={loading}
                  >
                    {loading ? "Searching..." : "Search"}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleClearFilters}
                  >
                    Clear Filters
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col md={12}>
          <MaterialTable
            title="Patients List"
            columns={[
              {
                title: "Name",
                field: "firstName",
                render: (rowData) => (
                  <span>
                    {rowData.firstName} {rowData.lastName}
                  </span>
                ),
                cellStyle: { width: 150, minWidth: 150 },
                headerStyle: { width: 150, minWidth: 150 },
              },
              {
                title: "Email",
                field: "email",
                cellStyle: { width: 150, minWidth: 150 },
                headerStyle: { width: 150, minWidth: 150 },
              },
              {
                title: "DOB",
                field: "dob",
                cellStyle: { width: 150, minWidth: 150 },
                headerStyle: { width: 150, minWidth: 150 },
              },
              {
                title: "Phone",
                field: "phoneNumber",
                cellStyle: { width: 150, minWidth: 150 },
                headerStyle: { width: 150, minWidth: 150 },
              },
              {
                title: "Location",
                field: "state",
                cellStyle: { width: 150, minWidth: 150 },
                headerStyle: { width: 150, minWidth: 150 },
              },
              {
                title: "Action",
                field: "action",
                filtering: false,
                render: (rowData) => (
                  <Button
                    onClick={() => navigatePatientProfile(rowData.id, rowData)}
                  >
                    View Details
                  </Button>
                ),
                cellStyle: { width: 200, minWidth: 200 },
                headerStyle: { width: 200, minWidth: 200 },
              },
            ]}
            data={tableData}
            options={{
              filtering: true,
              exportButton: true,
              headerStyle: { position: "sticky", top: 0, color: "black" },
              maxBodyHeight: "630px",
              search: false,
              pageSize: 100,
              pageSizeOptions: [50, 100, 200, 500],
              emptyRowsWhenPaging: false,
              draggable: false,
              toolbarButtonAlignment: "right",
              exportAllData: true,
              doubleHorizontalScroll: true,
              sorting: true,
            }}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default PatientSearchPage;
